import { useState } from "react";
import type {
  PortalApi,
  Quote,
  QuoteEntry,
  QuoteFilter,
  QuoteInputs,
  QuoteListPage,
  QuoteShare,
} from "../api.ts";
import { CopyButton, Id } from "../Id.tsx";
import { LocalTime } from "../LocalTime.tsx";
import { messageOf, useLoad } from "../load.ts";
import { moneyText } from "../money.ts";
import {
  addressLine,
  bestOffer,
  clientLink,
  clientName,
  quoteLink,
  type Savings,
  savingsOf,
  stageOf,
} from "../quotes.ts";
import {
  Badge,
  Button,
  Field,
  facts,
  Notice,
  PageLayout,
  Select,
  Table,
  TitleId,
  Toolbar,
} from "../ui.tsx";

/**
 * The quotes (docs/roadmap.md#admin-portal), for every role: every quote run so far, newest first,
 * with its client, property, best premium and what it saves against the client's current policy →
 * a quote with its carriers' premiums and every value it was priced on. Members see every quote,
 * as they see every client; "Only mine" narrows the list to the caller's.
 *
 * Addresses: `#/quotes`, `#/quotes/<quoteId>`.
 */

export function QuotesPage({ api, path }: { api: PortalApi; path: readonly string[] }) {
  const [id] = path;
  if (id) return <QuoteView key={id} api={api} quoteId={id} />;
  return <QuoteList api={api} />;
}

const STAGES = ["preliminary", "final"] as const;
const STATUSES = ["open", "presented", "won", "lost"] as const;

function QuoteList({ api }: { api: PortalApi }) {
  const [filter, setFilter] = useState<QuoteFilter>({});
  const first = useLoad(() => api.listQuotes(filter), [api, filter]);
  // Pages after the first, loaded with "Load more"; a new filter starts over.
  const [more, setMore] = useState<{ filter: QuoteFilter; pages: QuoteListPage[] }>();
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string>();
  const pages = first.data ? [first.data, ...(more?.filter === filter ? more.pages : [])] : [];
  const quotes = pages.flatMap((page) => page.quotes);
  const next = pages.at(-1)?.next;

  const change = (patch: { [K in keyof QuoteFilter]?: QuoteFilter[K] | undefined }) => {
    const merged = { ...filter, ...patch };
    setFilter(
      Object.fromEntries(
        Object.entries(merged).filter(([, value]) => value !== undefined && value !== false),
      ),
    );
    setMoreError(undefined);
  };
  const loadMore = async () => {
    if (!next) return;
    setLoadingMore(true);
    setMoreError(undefined);
    try {
      const page = await api.listQuotes(filter, next);
      setMore((current) => ({
        filter,
        pages: [...(current?.filter === filter ? current.pages : []), page],
      }));
    } catch (error) {
      setMoreError(messageOf(error));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <PageLayout title="Quotes">
      <Toolbar onSubmit={(event) => event.preventDefault()}>
        <Field label="Stage">
          <Select
            value={filter.stage ?? ""}
            onChange={(event) =>
              change({ stage: (event.target.value || undefined) as QuoteFilter["stage"] })
            }
          >
            <option value="">Any</option>
            {STAGES.map((stage) => (
              <option key={stage} value={stage}>
                {stage}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Status">
          <Select
            value={filter.status ?? ""}
            onChange={(event) =>
              change({ status: (event.target.value || undefined) as QuoteFilter["status"] })
            }
          >
            <option value="">Any</option>
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </Select>
        </Field>
        <label className="inline-flex items-center gap-2 font-medium text-fg-2 text-ui">
          <input
            type="checkbox"
            checked={filter.mine === true}
            onChange={(event) => change({ mine: event.target.checked })}
          />
          Only mine
        </label>
        <span className="text-muted text-ui">newest first; savings against the current policy</span>
      </Toolbar>
      {first.loading && <p className="text-muted">Loading…</p>}
      {first.error && <Notice tone="bad">{first.error}</Notice>}
      {first.data && quotes.length === 0 && !next && (
        <p className="text-muted">No quote matches.</p>
      )}
      {quotes.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th>Created</th>
              <th>Client</th>
              <th>Property</th>
              <th>Stage</th>
              <th>Status</th>
              <th>Current</th>
              <th>Best quote</th>
              <th>Savings</th>
              <th>Producer</th>
              <th>Quote</th>
            </tr>
          </thead>
          <tbody>
            {quotes.map((entry) => (
              <QuoteRow key={entry.quote.id} entry={entry} />
            ))}
          </tbody>
        </Table>
      )}
      {moreError && <Notice tone="bad">{moreError}</Notice>}
      {first.data && next && (
        <p>
          <Button type="button" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? "Loading…" : "Load more"}
          </Button>
          {quotes.length === 0 && (
            <span className="ml-3 text-muted text-ui">
              None among the newest quotes read; load more to look further back.
            </span>
          )}
        </p>
      )}
    </PageLayout>
  );
}

function QuoteRow({ entry }: { entry: QuoteEntry }) {
  const { quote, clients, properties, producer } = entry;
  const { run, current, savings } = bestOffer(entry);
  return (
    <tr>
      <td>
        <LocalTime iso={quote.createdAt} />
      </td>
      <td>
        {clients.length === 0
          ? "—"
          : clients.map((client, index) => (
              <span key={client.id}>
                {index > 0 && ", "}
                <a href={clientLink(client.id)}>{clientName(client)}</a>
              </span>
            ))}
      </td>
      <td>{properties.map(addressLine).filter(Boolean).join("; ") || "—"}</td>
      <td>
        <StageBadge quote={quote} />
      </td>
      <td>{quote.status ?? "—"}</td>
      <td>
        {current ? (
          <>
            {moneyText(current.premium)}
            {current.carrier && <span className="text-muted"> {current.carrier}</span>}
          </>
        ) : (
          "—"
        )}
      </td>
      <td>
        {run ? (
          <>
            {moneyText(run.premium as number)}
            {run.carrier && <span className="text-muted"> {run.carrier}</span>}
          </>
        ) : (
          <span className="text-muted">{entry.runs.length > 0 ? "not priced" : "no run"}</span>
        )}
      </td>
      <td>
        <SavingsText savings={savings} />
      </td>
      <td>{producer?.email ?? "—"}</td>
      <td>
        <Id id={quote.id} href={quoteLink(quote.id)} />
      </td>
    </tr>
  );
}

function StageBadge({ quote }: { quote: Quote }) {
  const stage = stageOf(quote);
  return <Badge tone={stage === "final" ? "ok" : "neutral"}>{stage}</Badge>;
}

function SavingsText({ savings }: { savings: Savings | undefined }) {
  if (!savings) return <>—</>;
  if (savings.amount >= 0) {
    return (
      <span className="font-medium text-ok">
        {moneyText(savings.amount)} ({savings.percent}%)
      </span>
    );
  }
  return (
    <span className="text-bad">
      costs {moneyText(-savings.amount)} more ({-savings.percent}%)
    </span>
  );
}

const INPUT_STATUSES = {
  assumed: { label: "assumed", tone: "bad", note: "a default: the client has not said" },
  evidence: { label: "evidence", tone: "neutral", note: "read from a record" },
  carrier: { label: "carrier", tone: "ok", note: "the carrier's own value" },
  confirmed: { label: "confirmed", tone: "ok", note: "the producer or the client said so" },
} as const;

function QuoteView({ api, quoteId }: { api: PortalApi; quoteId: string }) {
  const loaded = useLoad(() => api.quote(quoteId), [api, quoteId]);
  const back = { href: "#/quotes", label: "← Quotes" };
  if (loaded.loading) {
    return (
      <PageLayout title="Quote" back={back}>
        <p className="text-muted">Loading…</p>
      </PageLayout>
    );
  }
  if (!loaded.data) {
    return (
      <PageLayout title="Quote" back={back}>
        <Notice tone="bad">{loaded.error ?? "The quote was not found."}</Notice>
      </PageLayout>
    );
  }
  const entry = loaded.data;
  const { quote, clients, properties, producer, runs } = entry;
  const { current, run: best } = bestOffer(entry);
  const names = clients.map(clientName).join(" & ") || "no client";
  const preliminary = stageOf(quote) === "preliminary";
  return (
    <PageLayout
      title={
        <>
          Quote for {names}{" "}
          <TitleId>
            <Id id={quote.id} />
          </TitleId>
        </>
      }
      back={back}
    >
      {preliminary && (
        <Notice tone="info">
          Preliminary: priced on the client's loan, the county records and defaults, without asking
          the client. Check the assumed values below before you send it.
        </Notice>
      )}
      <dl className={facts}>
        <dt>Clients</dt>
        <dd>
          {clients.length === 0
            ? "—"
            : clients.map((client, index) => (
                <span key={client.id}>
                  {index > 0 && ", "}
                  <a href={clientLink(client.id)}>{clientName(client)}</a>
                </span>
              ))}
        </dd>
        <dt>Property</dt>
        <dd>{properties.map(addressLine).filter(Boolean).join("; ") || "—"}</dd>
        <dt>Stage · status</dt>
        <dd>
          <StageBadge quote={quote} /> {quote.status ?? "—"}
        </dd>
        <dt>Cover</dt>
        <dd>
          {[
            quote.lines,
            quote.dwellingCoverage !== undefined && `dwelling ${moneyText(quote.dwellingCoverage)}`,
            quote.deductible && `deductible ${quote.deductible}`,
            quote.liabilityLimit !== undefined && `liability ${moneyText(quote.liabilityLimit)}`,
          ]
            .filter(Boolean)
            .join(" · ") || "—"}
        </dd>
        <dt>Effective</dt>
        <dd>{quote.effectiveDate ?? "—"}</dd>
        <dt>Producer</dt>
        <dd>{producer?.email ?? "—"}</dd>
        <dt>Created</dt>
        <dd>
          <LocalTime iso={quote.createdAt} />
        </dd>
      </dl>

      <h2>Premiums</h2>
      <Table compact>
        <thead>
          <tr>
            <th>Policy</th>
            <th>Status</th>
            <th>Annual premium</th>
            <th>Savings</th>
            <th>Quote number</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              Current{current?.carrier && `: ${current.carrier}`}
              {current?.expires && <span className="text-muted"> (expires {current.expires})</span>}
            </td>
            <td>—</td>
            <td>{current ? moneyText(current.premium) : "not known"}</td>
            <td>—</td>
            <td>—</td>
          </tr>
          {runs.map((run) => (
            <tr key={run.id}>
              <td>
                {run.carrier ?? "Carrier"}
                {run === best && runs.length > 1 && <Badge tone="ok">best</Badge>}
              </td>
              <td>{run.status ?? "—"}</td>
              <td>{run.premium !== undefined ? moneyText(run.premium) : "—"}</td>
              <td>
                <SavingsText
                  savings={
                    run.status === "quoted" ? savingsOf(current?.premium, run.premium) : undefined
                  }
                />
              </td>
              <td>{run.carrierQuoteNumber ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      {runs.length === 0 && <p className="text-muted">No carrier has been run for this quote.</p>}

      <ShareLinks api={api} entry={entry} />

      <h2>Priced on</h2>
      <Inputs inputs={quote.inputs} />
    </PageLayout>
  );
}

/** The values the quote was priced on, the assumed ones first: those the client should confirm. */
function Inputs({ inputs }: { inputs: QuoteInputs | undefined }) {
  const entries = Object.entries(inputs ?? {});
  if (entries.length === 0) {
    return <p className="text-muted">The quote has no recorded inputs.</p>;
  }
  const order = Object.keys(INPUT_STATUSES);
  entries.sort(
    ([a, x], [b, y]) => order.indexOf(x.status) - order.indexOf(y.status) || a.localeCompare(b),
  );
  const assumed = entries.filter(([, input]) => input.status === "assumed").length;
  return (
    <>
      {assumed > 0 && (
        <p className="text-ui">
          {assumed} {assumed === 1 ? "value is" : "values are"} assumed: a default the client has
          not confirmed.
        </p>
      )}
      <Table>
        <thead>
          <tr>
            <th>Value</th>
            <th>As priced</th>
            <th>How sure</th>
            <th>Source</th>
            <th>As of</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([key, input]) => {
            const status = INPUT_STATUSES[input.status];
            return (
              <tr key={key}>
                <td>
                  <code>{key}</code>
                </td>
                <td>
                  {valueText(input.value)}
                  {input.was !== undefined && (
                    <span className="text-muted"> (was {valueText(input.was)})</span>
                  )}
                </td>
                <td title={status.note}>
                  <Badge tone={status.tone}>{status.label}</Badge>
                </td>
                <td>{input.source}</td>
                <td>{input.asOf}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
}

function valueText(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (typeof value === "number") return value.toLocaleString("en-US");
  return JSON.stringify(value);
}

const SHARE_DAYS = [7, 30, 90] as const;

/** Whether a link still works: not withdrawn, not expired. */
function linkState(share: QuoteShare, now: number): "works" | "withdrawn" | "expired" {
  if (share.revokedAt) return "withdrawn";
  return Date.parse(share.expiresAt) <= now ? "expired" : "works";
}

/**
 * The links made for the client: each opens a page with their current policy against the
 * premiums chosen and the savings, from a snapshot taken when it was made. The producer copies the
 * link into their own message; the first opening marks the quote presented.
 */
function ShareLinks({ api, entry }: { api: PortalApi; entry: QuoteEntry }) {
  const quoteId = entry.quote.id;
  const shares = useLoad(() => api.quoteShares(quoteId), [api, quoteId]);
  const quoted = entry.runs.filter((run) => run.status === "quoted" && run.premium !== undefined);
  const [chosen, setChosen] = useState<readonly string[]>(() => quoted.map((run) => run.id));
  const [days, setDays] = useState<number>(30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [made, setMade] = useState<QuoteShare>();

  const share = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const link = await api.shareQuote(quoteId, chosen, days);
      setMade(link);
      shares.set([link, ...(shares.data ?? [])]);
    } catch (reason) {
      setError(messageOf(reason));
    } finally {
      setBusy(false);
    }
  };
  const withdraw = async (token: string) => {
    setError(undefined);
    try {
      const withdrawn = await api.revokeShare(token);
      shares.set((shares.data ?? []).map((link) => (link.token === token ? withdrawn : link)));
      if (made?.token === token) setMade(undefined);
    } catch (reason) {
      setError(messageOf(reason));
    }
  };

  const now = Date.now();
  return (
    <>
      <h2>Share with the client</h2>
      {quoted.length === 0 ? (
        <p className="text-muted">There is no premium to share until a carrier has quoted.</p>
      ) : (
        <Toolbar
          onSubmit={(event) => {
            event.preventDefault();
            void share();
          }}
        >
          {quoted.map((run) => (
            <label key={run.id} className="inline-flex items-center gap-2 text-ui">
              <input
                type="checkbox"
                checked={chosen.includes(run.id)}
                onChange={(event) =>
                  setChosen((current) =>
                    event.target.checked
                      ? [...current, run.id]
                      : current.filter((id) => id !== run.id),
                  )
                }
              />
              {run.carrier ?? "Carrier"} {moneyText(run.premium as number)}
            </label>
          ))}
          <Field label="Works for">
            <Select value={days} onChange={(event) => setDays(Number(event.target.value))}>
              {SHARE_DAYS.map((count) => (
                <option key={count} value={count}>
                  {count} days
                </option>
              ))}
            </Select>
          </Field>
          <Button type="submit" variant="primary" disabled={busy || chosen.length === 0}>
            {busy ? "Making the link…" : "Make a link"}
          </Button>
        </Toolbar>
      )}
      {made && (
        <Notice tone="ok">
          Link ready: <code>{made.url}</code> <CopyButton text={made.url} label="Copy link" />
          <br />
          Paste it into your message to the client.
        </Notice>
      )}
      {error && <Notice tone="bad">{error}</Notice>}
      {shares.error && <Notice tone="bad">{shares.error}</Notice>}
      {shares.data && shares.data.length > 0 && (
        <Table compact>
          <thead>
            <tr>
              <th>Link</th>
              <th>Made</th>
              <th>Shows</th>
              <th>State</th>
              <th>Opened</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {shares.data.map((link) => {
              const state = linkState(link, now);
              return (
                <tr key={link.token}>
                  <td>
                    <span className="inline-flex items-baseline gap-1 whitespace-nowrap">
                      <code title={link.url}>…{link.token.slice(-6)}</code>
                      {state === "works" && <CopyButton text={link.url} label="Copy link" />}
                    </span>
                  </td>
                  <td>
                    <LocalTime iso={link.createdAt} />
                  </td>
                  <td>
                    {link.snapshot.offers
                      .map((offer) => `${offer.carrier} ${moneyText(offer.premium)}`)
                      .join(", ")}
                  </td>
                  <td>
                    {state === "works" ? (
                      <Badge tone="ok">
                        until <LocalTime iso={link.expiresAt} />
                      </Badge>
                    ) : (
                      <Badge>{state}</Badge>
                    )}
                  </td>
                  <td>
                    {link.viewedAt ? (
                      <>
                        {link.views}× · first <LocalTime iso={link.viewedAt} />
                      </>
                    ) : (
                      "not yet"
                    )}
                  </td>
                  <td>
                    {state === "works" && (
                      <Button
                        size="sm"
                        variant="quiet"
                        type="button"
                        onClick={() => withdraw(link.token)}
                      >
                        Withdraw
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </>
  );
}
